import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { db } from '@vibe/db/server';
import { AdListBoard, AdvertiserContractsBoard } from '@api/BoardSDK';
import { adMonthFinals, contractSkips } from '@generated/models';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';
import { pageShare } from '@generated/components/pages';
import { fillFromContracts, runsIn, monthIndex, CONTRACT_TAG } from '@generated/server/contracts.server';

const MONTH = z.string().regex(/^[A-Z][a-z]+ \d{4}$/);
export interface TallyAd { id: string; name: string; paper: string; adSize: string; section: string; cost: number; billed: string }

async function liveAds(month: string): Promise<TallyAd[]> {
  const board = new AdListBoard();
  const cols = ['issueMonth', 'paper', 'adSize', 'cost', 'section', 'specialSectionName', 'billed'] as const;
  const out: TallyAd[] = [];
  let res = await board.items().withColumns([...cols]).where({ issueMonth: { contains: month } }).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 100 }).execute();
  for (;;) {
    for (const i of res.items) {
      if ((i.issueMonth ?? '').trim() !== month) continue;
      out.push({ id: i.id, name: i.name, paper: i.paper ?? '', adSize: i.adSize ?? '', section: i.section === 'Special Section' && i.specialSectionName ? `Special: ${i.specialSectionName}` : i.section ?? 'ROP', cost: i.cost ?? 0, billed: i.billed ?? '' });
    }
    if (!res.cursor || out.length > 1000) break;
    res = await board.items().withColumns([...cols]).withPagination({ cursor: res.cursor }).execute();
  }
  return out;
}

/** Staff: a month's tally. Frozen copy if made final, otherwise the live running list. Also lists all finished months. */
export const getTally = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), month: MONTH }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const col = db.collection(adMonthFinals);
    const [final] = await col.where('month', '==', data.month).limit(1).get();
    const finals = (await col.orderBy('finalizedAt', 'desc').limit(100).get()).map((d) => ({ month: d.data.month, total: d.data.total, count: d.data.count, finalizedAt: d.data.finalizedAt }));
    if (final) return { month: data.month, final: true as boolean, finalizedAt: final.data.finalizedAt as string | null, ads: JSON.parse(final.data.items) as TallyAd[], finals };
    try { await fillFromContracts(data.month); } catch (e) { console.error('[tally] contract fill failed', e); }
    return { month: data.month, final: false as boolean, finalizedAt: null as string | null, ads: await liveAds(data.month), finals };
  }));

/** Staff: freeze the month's list as the permanent record. */
export const finalizeMonth = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), month: MONTH }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const col = db.collection(adMonthFinals);
    const [existing] = await col.where('month', '==', data.month).limit(1).get();
    if (existing) throw new Error('This month is already final.');
    const ads = await liveAds(data.month);
    await col.add({ month: data.month, items: JSON.stringify(ads), total: ads.reduce((a, x) => a + x.cost, 0), count: ads.length, finalizedAt: new Date().toISOString() });
    return { ok: true };
  }));

/** Staff: undo "final" so the month shows the live list again. */
export const reopenMonth = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), month: MONTH }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const col = db.collection(adMonthFinals);
    for (const d of await col.where('month', '==', data.month).get()) await col.doc(d.id).delete();
    return { ok: true };
  });

/** Staff: one row per finished month with per-paper totals (for the printable summary). */
export const getFinalSummary = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const docs = await db.collection(adMonthFinals).orderBy('finalizedAt', 'desc').limit(200).get();
    const rows = docs.map((d) => {
      let ads: TallyAd[] = [];
      try { ads = JSON.parse(d.data.items) as TallyAd[]; } catch { ads = []; }
      const sum = (p: string) => ads.filter((a) => a.paper === p).reduce((s, a) => s + a.cost, 0);
      const cnt = (p: string) => ads.filter((a) => a.paper === p).length;
      return { month: d.data.month, finalizedAt: d.data.finalizedAt, total: d.data.total, count: d.data.count,
        redHook: sum('Red Hook Star-Revue'), redHookCount: cnt('Red Hook Star-Revue'), village: sum('The Village Star-Revue'), villageCount: cnt('The Village Star-Revue') };
    });
    const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const key = (m: string) => { const [a, b] = m.split(' '); return Number(b) * 12 + MONTHS.indexOf(a ?? ''); };
    return { rows: rows.sort((a, b) => key(a.month) - key(b.month)) };
  });

const SUMMARY_MONTHS = (() => { const M = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']; const out: string[] = []; for (let i = 10; i < 24; i++) out.push(`${M[i % 12]} ${2026 + Math.floor(i / 12)}`); return out; })();

/** Staff: one row per issue, November 2026 – December 2027. Final months use their frozen totals; the rest are
 *  what's on the Ad List now plus contract ads (TFN and multi-month) that will be placed when that month opens. */
export const getTallyOverview = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const RH = 'Red Hook Star-Revue', VS = 'The Village Star-Revue';
    const adBoard = new AdListBoard();
    const [finals, contracts, skips, placed, live] = await Promise.all([
      db.collection(adMonthFinals).limit(200).get(),
      new AdvertiserContractsBoard().items().withColumns(['paper', 'adSize', 'monthlyPrice', 'startMonth', 'term', 'active']).where({ active: true }).withPagination({ limit: 100 }).execute(),
      db.collection(contractSkips).limit(1000).get(),
      (async () => { const out: { tag: string; month: string }[] = []; let r = await adBoard.items().withColumns(['portalOrderId', 'issueMonth']).where({ portalOrderId: { contains: CONTRACT_TAG } }).withPagination({ limit: 100 }).execute();
        for (;;) { out.push(...r.items.map((i) => ({ tag: i.portalOrderId ?? '', month: (i.issueMonth ?? '').trim() }))); if (!r.cursor || out.length > 3000) break; r = await adBoard.items().withColumns(['portalOrderId', 'issueMonth']).withPagination({ cursor: r.cursor }).execute(); } return out; })(),
      // Per-month ad rows (paper, size, cost) so the page count — not just money — can be totalled.
      Promise.all(SUMMARY_MONTHS.map(async (m) => {
        const cols = ['issueMonth', 'paper', 'adSize', 'cost'] as const;
        const out: { paper: string; adSize: string; cost: number }[] = [];
        let r = await adBoard.items().withColumns([...cols]).where({ issueMonth: { contains: m } }).withPagination({ limit: 100 }).execute();
        for (;;) {
          for (const i of r.items) if ((i.issueMonth ?? '').trim() === m) out.push({ paper: i.paper ?? '', adSize: i.adSize ?? '', cost: i.cost ?? 0 });
          if (!r.cursor || out.length > 1000) break;
          r = await adBoard.items().withColumns([...cols]).withPagination({ cursor: r.cursor }).execute();
        }
        return out;
      })),
    ]);
    const placedSet = new Set(placed.map((p) => `${p.tag}|${p.month}`));
    const skipSet = new Set(skips.map((s) => `${CONTRACT_TAG}${s.data.contractId}|${s.data.month}`));
    const rows = SUMMARY_MONTHS.map((month, i) => {
      const fin = finals.find((f) => f.data.month === month);
      if (fin) {
        let ads: TallyAd[] = []; try { ads = JSON.parse(fin.data.items) as TallyAd[]; } catch { ads = []; }
        const s = (p: string) => ads.filter((a) => a.paper === p);
        const pg = (p: string) => s(p).reduce((a, x) => a + pageShare(x.adSize), 0);
        const pc = (p: string) => s(p).filter((x) => pageShare(x.adSize) > 0).reduce((a, x) => a + x.cost, 0);
        return { month, final: true, finalizedAt: fin.data.finalizedAt, redHook: s(RH).reduce((a, x) => a + x.cost, 0), redHookCount: s(RH).length, redHookPages: pg(RH), redHookSized: pc(RH), village: s(VS).reduce((a, x) => a + x.cost, 0), villageCount: s(VS).length, villagePages: pg(VS), villageSized: pc(VS) };
      }
      const t = { [RH]: { sum: 0, n: 0, pages: 0, sized: 0 }, [VS]: { sum: 0, n: 0, pages: 0, sized: 0 } } as Record<string, { sum: number; n: number; pages: number; sized: number }>;
      const add = (paper: string, adSize: string, cost: number) => { const b = t[paper]; if (!b) return; const share = pageShare(adSize); b.sum += cost; b.n += 1; b.pages += share; if (share > 0) b.sized += cost; };
      for (const a of live[i] ?? []) add(a.paper, a.adSize, a.cost);
      const idx = monthIndex(month) ?? 0;
      for (const c of contracts.items) {
        const key = `${CONTRACT_TAG}${c.id}|${month}`;
        if (!runsIn(c, idx) || placedSet.has(key) || skipSet.has(key)) continue;
        add(c.paper ?? RH, c.adSize ?? '', c.monthlyPrice ?? 0);
      }
      return { month, final: false, finalizedAt: null as string | null, redHook: t[RH]?.sum ?? 0, redHookCount: t[RH]?.n ?? 0, redHookPages: t[RH]?.pages ?? 0, redHookSized: t[RH]?.sized ?? 0, village: t[VS]?.sum ?? 0, villageCount: t[VS]?.n ?? 0, villagePages: t[VS]?.pages ?? 0, villageSized: t[VS]?.sized ?? 0 };
    });
    return { rows };
  }));
